import { BookingAccountIdentity } from "./BookingAccountIdentity";
import { Check, Copy } from "lucide-react";
import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router";
import {
  providerApi,
  type ProviderBooking,
  type ProviderCustomerHistoryResponse,
  type ProviderSession,
} from "../../lib/api";
import { formatPricingAmount } from "../../lib/provider-pricing";
import { useAppPreferences, type TranslationFunction } from "../../lib/preferences";
import {
  formatBookingSchedule,
  getAvailableStatusActions,
  getBookingStatusLabel,
  getBookingStatusTone,
} from "./booking-utils";
import {
  buildReceiptPrintDocument,
  closeReceiptPrintWindow,
  openReceiptPrintWindow,
  printReceiptDocument,
} from "../../lib/receipt-print";

function providerLocale(language: "en" | "ar") {
  return language === "ar" ? "ar-EG" : "en-US";
}

function formatHistoryDateTime(value: string, timeZone: string, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatForPrint(value: string, timeZone: string, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function PrintInvoice({
  booking,
  session,
}: {
  booking: ProviderBooking;
  session: ProviderSession | null;
}) {
  const { direction, resolvedLanguage, t } = useAppPreferences();
  const locale = providerLocale(resolvedLanguage);
  const [printError, setPrintError] = useState<string | null>(null);

  const handlePrint = async () => {
    setPrintError(null);
    const popup = openReceiptPrintWindow();
    if (!popup) {
      setPrintError(t("Could not open the receipt print window. Allow popups and try again."));
      return;
    }

    try {
      const settings = await providerApi.getVenueSettings(booking.venue_id).catch(() => null);
      const total = formatPricingAmount(session?.elapsed_total_amount ?? booking.scheduled_total_amount);
      const documentHtml = buildReceiptPrintDocument({
        direction,
        language: resolvedLanguage,
        paperWidth: settings?.receipt_paper_width === "58mm" ? "58mm" : "80mm",
        title: t("Receipt"),
        venueName: booking.venue_name,
        receiptNumber: booking.id.slice(0, 8).toUpperCase(),
        issuedAt: formatForPrint(new Date().toISOString(), booking.venue_timezone, locale),
        customerName: booking.customer_name,
        lines: [{
          description: `${booking.unit_name} · ${formatBookingSchedule(booking, locale, t("to"))}`,
          quantity: "1",
          total,
        }],
        subtotal: total,
        serviceFee: formatPricingAmount(0),
        tax: formatPricingAmount(0),
        total,
        paid: total,
        balance: formatPricingAmount(0),
        footer: t("This receipt was generated by Gaamed. For questions contact your venue directly."),
        labels: {
          customer: t("Customer"),
          issued: t("Issued"),
          line: t("Line"),
          qty: t("Qty"),
          total: t("Total"),
          subtotal: t("Subtotal"),
          service: t("Service"),
          vat: t("VAT"),
          paid: t("Paid"),
          balance: t("Balance"),
        },
      });
      const printed = await printReceiptDocument(popup, documentHtml);
      if (!printed) {
        setPrintError(t("Could not print the receipt. Allow popups and try again."));
      }
    } catch {
      closeReceiptPrintWindow(popup);
      setPrintError(t("Could not prepare the receipt for printing."));
    }
  };

  return (
    <>
      {printError ? <p className="text-xs text-rose-200">{printError}</p> : null}
      <button
        className="ops-button"
        data-size="sm"
        data-variant="secondary"
        onClick={handlePrint}
        type="button"
      >
        {t("Print receipt")}
      </button>
    </>
  );
}


function getHistoryMatchLabel(matchedOn: string, t: TranslationFunction) {
  if (matchedOn === "account") return t("Linked account history");
  if (matchedOn === "email") return t("Matched by email");
  if (matchedOn === "phone") return t("Matched by phone");
  return t("Matched by customer name");
}

export function BookingDetailCard({
  activeSession,
  booking,
  isStartingSession,
  isUpdatingStatus,
  onEdit,
  onStartSession,
  onStatusChange,
  readOnly = false,
}: {
  activeSession: ProviderSession | null;
  booking: ProviderBooking;
  isStartingSession: boolean;
  isUpdatingStatus: boolean;
  onEdit: () => void;
  onStartSession: (note: string) => Promise<void>;
  onStatusChange: (status: ProviderBooking["status"], note: string) => Promise<void>;
  readOnly?: boolean;
}) {
  const { resolvedLanguage, t } = useAppPreferences();
  const locale = providerLocale(resolvedLanguage);
  const [note, setNote] = useState(booking.status_note ?? "");
  const [customerHistory, setCustomerHistory] =
    useState<ProviderCustomerHistoryResponse | null>(null);
  const [isLoadingCustomerHistory, setIsLoadingCustomerHistory] = useState(false);
  const [customerHistoryError, setCustomerHistoryError] = useState<string | null>(null);
  const [copiedQr, setCopiedQr] = useState(false);

  useEffect(() => {
    setNote(booking.status_note ?? "");
  }, [booking.id, booking.status_note]);

  useEffect(() => {
    if (readOnly) {
      setCustomerHistory(null);
      setCustomerHistoryError(null);
      setIsLoadingCustomerHistory(false);
      return;
    }
    let isActive = true;

    setIsLoadingCustomerHistory(true);
    setCustomerHistoryError(null);
    setCustomerHistory(null);

    void providerApi
      .getCustomerHistory({
        customerName: booking.customer_name,
        customerUserId: booking.customer_user_id,
        customerEmail: booking.customer_email,
        customerPhone: booking.customer_phone,
        excludeBookingId: booking.id,
      })
      .then((response) => {
        if (!isActive) {
          return;
        }

        setCustomerHistory(response);
      })
      .catch((error: unknown) => {
        if (!isActive) {
          return;
        }

        setCustomerHistoryError(
          error instanceof Error ? error.message : "Unable to load customer history.",
        );
      })
      .finally(() => {
        if (!isActive) {
          return;
        }

        setIsLoadingCustomerHistory(false);
      });

    return () => {
      isActive = false;
    };
  }, [
    booking.customer_user_id,
    booking.customer_name,
    booking.customer_email,
    booking.customer_phone,
    booking.id,
    readOnly,
  ]);

  const actions = getAvailableStatusActions(booking.status);
  const historyItemCount =
    (customerHistory?.bookings.length ?? 0) + (customerHistory?.sessions.length ?? 0);
  const lastTouchpointAt = customerHistory
    ? [
        ...customerHistory.bookings.map((b) => b.scheduled_start_at),
        ...customerHistory.sessions.map((s) => s.started_at),
      ].sort().at(-1) ?? null
    : null;
  // Deep link into the Customers section: prefill its search and auto-select
  // the matching record (see ProviderCustomersCard).
  const customerHistorySearch = new URLSearchParams();
  if (booking.customer_user_id) customerHistorySearch.set("customer_user_id", booking.customer_user_id);
  if (booking.customer_email) customerHistorySearch.set("customer_email", booking.customer_email);
  customerHistorySearch.set("customer_name", booking.customer_name);
  if (booking.customer_phone) customerHistorySearch.set("customer_phone", booking.customer_phone);
  const showCheckInQr =
    activeSession !== null ||
    booking.status === "pending" ||
    booking.status === "confirmed" ||
    booking.status === "checked_in";

  const handleCopyQr = async () => {
    try {
      await navigator.clipboard.writeText(booking.admit_qr_payload);
      setCopiedQr(true);
      setTimeout(() => setCopiedQr(false), 1200);
    } catch {
      // Ignore copy failures
    }
  };

  // Guests check in with this QR on arrival: show it open when they're due within the hour.
  const startsInMinutes = (new Date(booking.scheduled_start_at).getTime() - Date.now()) / 60000;
  const qrOpenByDefault = activeSession !== null || booking.status === "checked_in" || (booking.status === "confirmed" && startsInMinutes <= 60);
  const hasActions = !readOnly && (actions.length > 0 || ((booking.status === "pending" || booking.status === "confirmed") && !activeSession));

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-start justify-between gap-3 pe-8">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-white">{booking.customer_name}</h3>
          <p className="ops-copy text-sm">
            {booking.unit_name} · {formatBookingSchedule(booking, locale, t("to"))}
          </p>
        </div>
        <span
          className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] ${getBookingStatusTone(booking.status)}`}
        >
          {t(getBookingStatusLabel(booking.status))}
        </span>
      </div>

      {booking.status === "pending" && !readOnly ? (
        <p className="ops-banner" data-tone="warning">{t("The player is waiting for you to confirm or decline this request.")}</p>
      ) : null}

      {activeSession ? (
        <div className="ops-banner flex flex-wrap items-center justify-between gap-2" data-tone="info">
          <div className="grid gap-0.5">
            <p className="ops-label">{t("Playing now")}</p>
            <p className="text-xs">
              {t("Started at {value}", {
                value: new Intl.DateTimeFormat(locale, {
                  timeZone: booking.venue_timezone,
                  hour: "numeric",
                  minute: "2-digit",
                }).format(new Date(activeSession.started_at)),
              })}
              {activeSession.planned_end_at
                ? ` · ${t("planned end {value}", { value: new Intl.DateTimeFormat(locale, {
                    timeZone: booking.venue_timezone,
                    hour: "numeric",
                    minute: "2-digit",
                  }).format(new Date(activeSession.planned_end_at)) })}`
                : ""}
            </p>
          </div>
          <p className="text-xs font-semibold">
            {t("Time so far {value}", { value: formatPricingAmount(activeSession.elapsed_total_amount) })}
          </p>
        </div>
      ) : null}

      {hasActions ? (
        <div className="booking-actions grid gap-2">
          <div className="flex flex-wrap gap-2">
            {booking.status === "confirmed" && !activeSession && (
              <button
                className="ops-button"
                data-variant="primary"
                disabled={isStartingSession}
                onClick={() => void onStartSession(note)}
                type="button"
              >
                {isStartingSession ? t("Starting...") : t("Start session")}
              </button>
            )}
            {actions.map((action) => (
              <button
                className="ops-button"
                data-variant={
                  action.tone === "danger"
                    ? "danger"
                    : action.tone === "warning"
                      ? "warning"
                      : "primary"
                }
                disabled={isUpdatingStatus}
                key={action.nextStatus}
                onClick={() => void onStatusChange(action.nextStatus, note)}
                type="button"
              >
                {isUpdatingStatus ? t("Saving...") : t(action.label)}
              </button>
            ))}
            {(booking.status === "pending" || booking.status === "confirmed") && !activeSession && (
              <button
                className="ops-button"
                data-variant="secondary"
                onClick={onEdit}
                type="button"
              >
                {t("Edit booking")}
              </button>
            )}
          </div>
          <label className="ops-field">
            <span className="ops-label">{t("Action note")}</span>
            <input
              className="ops-input"
              onChange={(event) => setNote(event.target.value)}
              placeholder={t("Optional: why, for the record (e.g. guest called to cancel)")}
              value={note}
            />
          </label>
        </div>
      ) : null}

      <dl className="booking-facts">
        <div>
          <dt>{t("Phone")}</dt>
          <dd dir="ltr">
            {booking.customer_phone ? <a href={`tel:${booking.customer_phone}`}>{booking.customer_phone}</a> : t("No contact")}
          </dd>
        </div>
        <div>
          <dt>{t("Email")}</dt>
          <dd className="break-all" dir="ltr">{booking.customer_email || t("No contact")}</dd>
        </div>
        <div>
          <dt>{t("Booking total")}</dt>
          <dd>{formatPricingAmount(booking.scheduled_total_amount)}</dd>
        </div>
        {booking.notes ? (
          <div>
            <dt>{t("Notes")}</dt>
            <dd>{booking.notes}</dd>
          </div>
        ) : null}
      </dl>

      {!readOnly ? (
        <div className="booking-history">
          {isLoadingCustomerHistory ? (
            <p className="ops-copy text-xs">{t("Loading customer history...")}</p>
          ) : customerHistoryError ? (
            <p className="text-xs text-rose-200">{customerHistoryError}</p>
          ) : customerHistory && historyItemCount === 0 ? (
            <p className="ops-copy text-xs">
              <span className="font-semibold">{getHistoryMatchLabel(customerHistory.matched_on, t)}</span>
              {": "}
              {t("First visit: no earlier bookings or sessions with you.")}
            </p>
          ) : customerHistory ? (
            <>
              <p className="ops-copy text-xs">
                <span className="font-semibold">{getHistoryMatchLabel(customerHistory.matched_on, t)}</span>
                {": "}
                {t("{count} bookings", { count: customerHistory.bookings.length })}
                {" · "}
                {t("{count} sessions", { count: customerHistory.sessions.length })}
                {lastTouchpointAt
                  ? ` · ${t("Last seen")} ${formatHistoryDateTime(lastTouchpointAt, booking.venue_timezone, locale)}`
                  : ""}
              </p>
              <Link className="ops-button" data-size="sm" data-variant="secondary" to={`/provider/customers?${customerHistorySearch.toString()}`}>
                {t("View customer")} →
              </Link>
            </>
          ) : null}
        </div>
      ) : null}

      {showCheckInQr ? (
        <details className="booking-disclosure" open={qrOpenByDefault}>
          <summary>{t(booking.customer_user_id === null ? "Staff check-in QR" : "Customer check-in QR")}</summary>
          <p className="ops-copy text-xs">
            {t(booking.customer_user_id === null ? "Use the provider scanner to check in this unlinked booking." : "Show this to the customer — they scan it with the gaamed app.")}
          </p>
          <div className="flex flex-col items-center gap-2">
            <div className="rounded-xl bg-white p-2 shadow-[0_12px_24px_rgba(15,23,42,0.2)]">
              <QRCodeSVG
                bgColor="#ffffff"
                fgColor="#0f172a"
                level="M"
                size={192}
                value={booking.admit_qr_payload}
              />
            </div>
            <button
              aria-label={t("Copy check-in QR")}
              className="ops-button"
              data-size="sm"
              data-variant="secondary"
              onClick={() => void handleCopyQr()}
              type="button"
            >
              {copiedQr ? <Check size={14} /> : <Copy size={14} />} {copiedQr ? t("Copied") : t("Copy code")}
            </button>
          </div>
        </details>
      ) : null}

      {(booking.status === "completed" || booking.status === "checked_in" || booking.status === "confirmed") && !readOnly ? (
        <div className="flex flex-wrap gap-2">
          <PrintInvoice booking={booking} session={activeSession} />
        </div>
      ) : null}

      <details className="booking-disclosure">
        <summary>{t("Account details")}</summary>
        <BookingAccountIdentity booking={booking} />
      </details>
    </div>
  );
}
